import { getInventoryItems, VideoListItem } from 'lib/api';
import { CreateVideoResponse } from 'lib/api/videos/useCreateVideoMutation';
import { ICombinedUserData, VideoListAutomotiveItem } from 'lib/context';
import { VideoData } from 'lib/hooks';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, any>>;
  }
}

function hasDataLayer(): boolean {
  return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
}

export function pushToDataLayer(data: Record<string, any>) {
  if (!hasDataLayer()) {
    return;
  }

  window.dataLayer?.push({
    ...data,
  });
}

interface VehicleAttributes {
  vehicle?: string;
  price?: number;
  vin?: string;
  stock_number?: string;
  condition?: string;
  engine?: string;
  make?: string;
  model?: string;
  trim?: string;
  year?: number;
  mileage?: number;
  exterior_color?: string;
  interior_color?: string;
  transmission?: string;
}

async function fetchInventoryByVin(vin: string | undefined) {
  if (!vin) return null;
  try {
    const resp = await getInventoryItems({
      limit: 10,
      start: 0,
      sort: '',
      search: vin,
      soldFilter: '2',
    });
    const item = resp?.items?.[0];
    if (!item) return null;

    const vehicle: VehicleAttributes = {
      vehicle: item.title,
      price: item.price ? parseFloat(item.price) : undefined,
      vin: item.vin,
      stock_number: item.stockNumber,
      condition: item.itemCondition || undefined,
      engine: item.engine || undefined,
      make: item.make,
      model: item.model,
      trim: item.trim || undefined,
      year: item.year ? parseInt(item.year, 10) : undefined,
      mileage: item.mileage ? parseInt(item.mileage, 10) : undefined,
      exterior_color: item.exteriorColor || undefined,
      interior_color: item.interiorColor || undefined,
      transmission: item.transmission || undefined,
    };
    return vehicle;
  } catch (err) {
    console.error('Failed to fetch inventory for VIN:', vin, err);
    return null;
  }
}

type Action =
  | 'Pin'
  | 'Unpin'
  | 'Duplicate'
  | 'Trim'
  | 'Download'
  | 'Merge'
  | 'Archive'
  | 'Unarchive'
  | 'Copy'
  | 'Move'
  | 'Delete'
  | 'Copy Video'
  | 'Save Merge'
  | 'Save Voiceover'
  | 'Review recording screen';

export async function trackVideoActionEvent({
  videoAction,
  video,
  userData,
  event,
  creationType,
}: {
  videoAction?: Action;
  creationType?: string;
  video: VideoListAutomotiveItem | CreateVideoResponse;
  userData: ICombinedUserData;
  event: 'Video Interaction' | 'Save new video';
}) {
  if (!hasDataLayer()) {
    return;
  }
  const vehicleData = await fetchInventoryByVin(
    video?.videoRequest?.inventoryItem?.vin
  );
  pushToDataLayer({
    event,
    page_application: 'CDK Video Whitelabel',
    video_id: video.id,
    video_title: video.title,
    ...(creationType !== undefined && { creation_type: creationType }),
    ...(videoAction !== undefined && { video_action: videoAction }),

    ...(vehicleData ?? {}),

    user_id: userData.email,
    page_url: window.location.href,
    is_internal_user:
      userData.email.includes('cdk') || userData.email.includes('covideo'),
  });
}

export async function trackScheduleVideoMessageEvent({
  event,
  userData,
  data,
}: {
  event: 'Schedule Video Message' | 'Send Video Message';
  userData: ICombinedUserData;
  data?: {
    videoId: string | undefined;
    customerId: string | undefined;
    sms: boolean;
    email: boolean;
    notificationViewed: boolean;
    landingPage: string | undefined;
    callToAction: string | undefined;
    vin: string | undefined;
  };
}) {
  if (!hasDataLayer()) {
    return;
  }
  const vehicleData = await fetchInventoryByVin(data?.vin);
  pushToDataLayer({
    event: event,
    page_application: 'CDK Video Whitelabel',
    video_id: data?.videoId || undefined,
    customer_id: data?.customerId || undefined,
    sms: data?.sms || undefined,
    email: data?.email || undefined,
    notification_viewed: data?.notificationViewed || false,
    landing_page: data?.landingPage || undefined,
    call_to_action: data?.callToAction || undefined,
    ...(vehicleData ?? {}),
    user_id: userData.email,
    page_url: window.location.href,
    is_internal_user:
      userData.email.includes('cdk') || userData.email.includes('covideo'),
  });
}
export async function trackVideoInfoUpdate({
  userData,
  video,
  updateAction,
  vin,
  event,
}: {
  updateAction: string;
  event: 'Update Vehicle Info' | 'Update Video Info';
  video: VideoData;
  userData: ICombinedUserData;
  vin?: string;
}) {
  if (!hasDataLayer()) {
    return;
  }
  const { email } = userData;
  const vehicleData = await fetchInventoryByVin(vin);
  pushToDataLayer({
    page_application: 'CDK Video Whitelabel',
    video_title: video.title,
    event,
    update_action: updateAction,
    video_id: video.id,
    ...(vehicleData ?? {}),
    user_id: email,
    page_url: window.location.href,
    is_internal_user: email.includes('cdk') || email.includes('covideo'),
  });
}

export async function trackShareVideoActionEvent({
  videoAction,
  video,
  userData,
  data,
  shareOption,
}: {
  videoAction: 'Share Video Message';
  video: VideoListItem;
  userData: ICombinedUserData;
  shareOption: 'Copy Video URL' | 'Copy Raw HTML';
  data?: {
    videoId: string | undefined;
    customerId: string | undefined;
    sms: boolean;
    email: boolean;
    notificationViewed: boolean;
    landingPage: string | undefined;
    callToAction: string | undefined;
    vin: string | undefined;
    thumbnails: string | undefined;
  };
}) {
  if (!hasDataLayer()) {
    return;
  }
  const vehicleData = await fetchInventoryByVin(data?.vin);
  pushToDataLayer({
    page_application: 'CDK Video Whitelabel',
    event: videoAction,
    video_id: video.id,
    video_title: video.title,
    share_option: shareOption,
    customer_id: data?.customerId || undefined,
    sms: data?.sms || undefined,
    email: data?.email || undefined,
    notification_viewed: data?.notificationViewed || false,
    landing_page: data?.landingPage || undefined,
    call_to_action: data?.callToAction || undefined,
    ...(vehicleData ?? {}),
    thumbnails: data?.thumbnails || undefined,

    user_id: userData.email,
    page_url: window.location.href,
    is_internal_user:
      userData.email.includes('cdk') || userData.email.includes('covideo'),
  });
}
